import React, { useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/ui/AppHeader';
import HubQuickSearch from '../components/search/HubQuickSearch';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchDesignTrackerHub } from '../utils/hubSearch/searchSecondaryHubs';
import { HubSearchResult } from '../types/hubSearch';
import { useCRM } from '../utils/CRMContext';
import { DESIGN_SUB_STAGES, DesignSubStage } from '../types';
import { getProjectDisplayName } from '../utils/projectName';
import { getAssignedDesigner, listDesigners } from '../utils/dealDesignerArchitect';
import { db } from '../utils/firebase';
import { doc, updateDoc, deleteField, Timestamp, type DocumentData, type UpdateData } from 'firebase/firestore';
import {
    PenTool, ArrowLeft, ChevronRight, ChevronLeft,
    Clock, Inbox, CalendarClock, User
} from 'lucide-react';
import './DesignTracker.css';

// ── Business day utilities ──
const addBusinessDays = (startDate: Date, days: number): Date => {
    const result = new Date(startDate);
    let added = 0;
    while (added < days) {
        result.setDate(result.getDate() + 1);
        const dow = result.getDay();
        if (dow !== 0 && dow !== 6) added++;
    }
    return result;
};

const getBusinessDaysBetween = (start: Date, end: Date): number => {
    let count = 0;
    const current = new Date(start);
    current.setHours(0, 0, 0, 0);
    const target = new Date(end);
    target.setHours(0, 0, 0, 0);
    const direction = target >= current ? 1 : -1;

    if (direction === 1) {
        while (current < target) {
            current.setDate(current.getDate() + 1);
            const dow = current.getDay();
            if (dow !== 0 && dow !== 6) count++;
        }
    } else {
        while (current > target) {
            current.setDate(current.getDate() - 1);
            const dow = current.getDay();
            if (dow !== 0 && dow !== 6) count--;
        }
    }
    return count * direction;
};

const SUB_STAGE_COLORS: Record<DesignSubStage, string> = {
    'payment-requested': '#F59E0B',
    'in-design': '#8B5CF6',
    'design-review': '#EC4899',
    'revisions': '#F97316',
    'design-complete': '#22C55E',
};

const SUB_STAGE_LABELS: Record<DesignSubStage, string> = {
    'payment-requested': 'Payment Requested',
    'in-design': 'In Design',
    'design-review': 'Design Review',
    'revisions': 'Revisions',
    'design-complete': 'Design Complete',
};

const DesignTracker: React.FC = () => {
    const { enhancedDeals, updateEnhancedDeal, pipelines, crmUsers, engagementLetters } = useCRM();
    const navigate = useNavigate();
    const [selectedSubStage, setSelectedSubStage] = useState<DesignSubStage | null>(null);
    const [showQuickSearch, setShowQuickSearch] = useState(false);

    /** Deal/lead ids with a voided engagement letter — drop from unpaid / fee totals. */
    const voidedDealAndLeadIds = useMemo(() => {
        const dealIds = new Set<string>();
        const leadIds = new Set<string>();
        for (const letter of engagementLetters) {
            if (letter.status !== 'voided') continue;
            if (letter.dealId) dealIds.add(letter.dealId);
            if (letter.leadId) leadIds.add(letter.leadId);
        }
        return { dealIds, leadIds };
    }, [engagementLetters]);

    const dealHasVoidedLetter = useCallback((deal: { id?: string; leadId?: string }) => {
        if (deal.id && voidedDealAndLeadIds.dealIds.has(deal.id)) return true;
        if (deal.leadId && voidedDealAndLeadIds.leadIds.has(deal.leadId)) return true;
        return false;
    }, [voidedDealAndLeadIds]);

    useHubQuickSearchHotkey({
        onOpen: () => setShowQuickSearch(true),
        blocked: showQuickSearch,
    });

    const getDesignQuickSearchResults = useCallback(
        (query: string) => searchDesignTrackerHub(query, enhancedDeals),
        [enhancedDeals],
    );

    const handleDesignQuickSearchSelect = useCallback(
        (result: HubSearchResult) => {
            const deal = result.payload as { id?: string };
            if (deal.id) navigate('/pipeline', { state: { selectedDealId: deal.id } });
        },
        [navigate],
    );

    const designers = useMemo(() => listDesigners(crmUsers), [crmUsers]);

    const handleAssignDesigner = useCallback(async (dealId: string, designerUid: string) => {
        const designer = crmUsers.find(u => u.uid === designerUid);
        const patch: UpdateData<DocumentData> = {
            assignedDesigner: designerUid ? designerUid : deleteField(),
            assignedDesignerName: designer?.displayName ? designer.displayName : deleteField(),
            updatedAt: Timestamp.now(),
        };
        if (designerUid) patch.assignedArchitect = deleteField();
        await updateDoc(doc(db, 'enhancedDeals', dealId), patch);
    }, [crmUsers]);

    // Get only deals in the "design" stage
    const designDeals = useMemo(() => {
        return enhancedDeals.filter(d => d.stageId === 'design' && d.outcome === 'active');
    }, [enhancedDeals]);

    const isAwaitingPaymentDeal = useCallback((deal: { designSubStage?: DesignSubStage }) => {
        const sub = deal.designSubStage;
        return sub === 'payment-requested' || !sub;
    }, []);

    // Count deals per sub-stage (voided letters drop out of unpaid / payment-requested)
    const subStageCounts = useMemo(() => {
        const counts: Record<DesignSubStage, number> = {
            'payment-requested': 0,
            'in-design': 0,
            'design-review': 0,
            'revisions': 0,
            'design-complete': 0,
        };
        for (const deal of designDeals) {
            const sub = (deal as any).designSubStage as DesignSubStage | undefined;
            if (isAwaitingPaymentDeal(deal) && dealHasVoidedLetter(deal)) {
                continue;
            }
            if (sub && sub in counts) {
                counts[sub]++;
            } else {
                // Deals without a sub-stage default to payment-requested
                counts['payment-requested']++;
            }
        }
        return counts;
    }, [designDeals, dealHasVoidedLetter, isAwaitingPaymentDeal]);

    // Unpaid design fees — exclude voided agreements
    const totalDesignFees = useMemo(() => {
        return designDeals.reduce((sum, d) => {
            if (!isAwaitingPaymentDeal(d as any)) return sum;
            if (dealHasVoidedLetter(d)) return sum;
            const fee = (d as any).designFee as number | undefined;
            return sum + (fee || 0);
        }, 0);
    }, [designDeals, dealHasVoidedLetter, isAwaitingPaymentDeal]);

    // Filtered deals
    const filteredDeals = useMemo(() => {
        if (!selectedSubStage) {
            return designDeals.filter(d => {
                if (isAwaitingPaymentDeal(d as any) && dealHasVoidedLetter(d)) return false;
                return true;
            });
        }
        return designDeals.filter(d => {
            const sub = (d as any).designSubStage as DesignSubStage | undefined;
            if (selectedSubStage === 'payment-requested') {
                if (dealHasVoidedLetter(d)) return false;
                return sub === 'payment-requested' || !sub;
            }
            return sub === selectedSubStage;
        });
    }, [designDeals, selectedSubStage, dealHasVoidedLetter, isAwaitingPaymentDeal]);

    // Advance / Regress sub-stage
    const advanceSubStage = useCallback(async (dealId: string, currentSub: DesignSubStage | undefined) => {
        const currentIdx = DESIGN_SUB_STAGES.findIndex(s => s.id === (currentSub || 'payment-requested'));
        const nextIdx = currentIdx + 1;
        if (nextIdx >= DESIGN_SUB_STAGES.length) {
            // Move to estimated stage
            await updateEnhancedDeal(dealId, {
                stageId: 'estimated',
                designSubStage: undefined as any,
            } as any);
            return;
        }
        const nextSubStage = DESIGN_SUB_STAGES[nextIdx].id;
        const updates: any = { designSubStage: nextSubStage };
        // Auto-stamp payment date when moving to 'in-design'
        if (nextSubStage === 'in-design') {
            updates.designPaymentDate = new Date();
        }
        await updateEnhancedDeal(dealId, updates);
    }, [updateEnhancedDeal]);

    const regressSubStage = useCallback(async (dealId: string, currentSub: DesignSubStage | undefined) => {
        const currentIdx = DESIGN_SUB_STAGES.findIndex(s => s.id === (currentSub || 'payment-requested'));
        if (currentIdx <= 0) return;
        const prevSubStage = DESIGN_SUB_STAGES[currentIdx - 1].id;
        await updateEnhancedDeal(dealId, {
            designSubStage: prevSubStage,
        } as any);
    }, [updateEnhancedDeal]);

    // Calculate days in current sub-stage
    const getDaysInStage = (deal: any): number => {
        const enteredAt = deal.currentStageEnteredAt;
        if (!enteredAt) return 0;
        const d = enteredAt instanceof Date ? enteredAt : enteredAt?.toDate?.();
        if (!d) return 0;
        const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
        return Math.min(days, 999);
    };

    // Calculate business days remaining until design delivery deadline
    const getDeadlineInfo = (deal: any): { businessDaysLeft: number; deadlineDate: Date; hasPaymentDate: boolean } | null => {
        const paymentDate = deal.designPaymentDate;
        if (!paymentDate) return null;
        const d = paymentDate instanceof Date ? paymentDate : paymentDate?.toDate?.();
        if (!d) return null;
        const deadlineDate = addBusinessDays(d, 10);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const businessDaysLeft = getBusinessDaysBetween(today, deadlineDate);
        return { businessDaysLeft, deadlineDate, hasPaymentDate: true };
    };

    // Edit payment date
    const handlePaymentDateChange = useCallback(async (dealId: string, dateStr: string) => {
        if (!dateStr) return;
        const newDate = new Date(dateStr + 'T12:00:00');
        await updateEnhancedDeal(dealId, { designPaymentDate: newDate } as any);
    }, [updateEnhancedDeal]);

    // Progress percentage for a deal
    const getProgress = (sub: DesignSubStage | undefined): number => {
        const idx = DESIGN_SUB_STAGES.findIndex(s => s.id === (sub || 'payment-requested'));
        return Math.round(((idx + 1) / DESIGN_SUB_STAGES.length) * 100);
    };

    const getSubStageName = (sub: DesignSubStage | undefined): string => {
        const found = DESIGN_SUB_STAGES.find(s => s.id === (sub || 'payment-requested'));
        return found?.name || 'Payment Requested';
    };

    const handleCardClick = (deal: any) => {
        if (deal.id) {
            navigate('/pipeline', { state: { selectedDealId: deal.id } });
        }
    };

    return (
        <div className="design-tracker-page">
            <AppHeader />
            <div className="design-tracker-container">
                {/* Header */}
                <div className="design-tracker-header">
                    <div className="design-tracker-title-row">
                        <div>
                            <h1 className="design-tracker-title">
                                <PenTool size={22} />
                                Design Tracker
                            </h1>
                            <Link to="/pipeline" className="design-tracker-back-link">
                                <ArrowLeft size={14} />
                                Back to Pipeline Board
                            </Link>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="design-tracker-stats">
                        <div className="design-stat-card">
                            <span className="design-stat-label">In Design</span>
                            <span className="design-stat-value">{designDeals.length}</span>
                        </div>
                        <div className="design-stat-card">
                            <span className="design-stat-label">Awaiting Payment</span>
                            <span className="design-stat-value">{subStageCounts['payment-requested']}</span>
                        </div>
                        <div className="design-stat-card">
                            <span className="design-stat-label">Actively Designing</span>
                            <span className="design-stat-value">{subStageCounts['in-design'] + subStageCounts['design-review'] + subStageCounts['revisions']}</span>
                        </div>
                        <div className="design-stat-card">
                            <span className="design-stat-label">Complete</span>
                            <span className="design-stat-value green">{subStageCounts['design-complete']}</span>
                        </div>
                        {totalDesignFees > 0 && (
                            <div className="design-stat-card">
                                <span className="design-stat-label">Design Fees</span>
                                <span className="design-stat-value green">${totalDesignFees.toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Step Timeline */}
                <div className="design-timeline">
                    <div className="design-timeline-track">
                        {DESIGN_SUB_STAGES.map((stage, index) => {
                            const count = subStageCounts[stage.id];
                            const isSelected = selectedSubStage === stage.id;

                            // Determine which is the "furthest active" sub-stage
                            const activeIdx = DESIGN_SUB_STAGES.reduce((max, s, i) =>
                                subStageCounts[s.id] > 0 ? i : max, -1);
                            const isCompleted = index < activeIdx;
                            const isActive = index === activeIdx;

                            const stepClasses = [
                                'design-timeline-step',
                                isCompleted ? 'completed' : '',
                                isActive ? 'active' : '',
                                isSelected ? 'selected' : '',
                                count > 0 ? 'has-deals' : '',
                            ].filter(Boolean).join(' ');

                            return (
                                <div
                                    key={stage.id}
                                    className={stepClasses}
                                    onClick={() => setSelectedSubStage(isSelected ? null : stage.id)}
                                >
                                    <div className="design-timeline-node">
                                        <svg className="design-timeline-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        <span className="design-timeline-count">{count}</span>
                                    </div>
                                    <span className="design-timeline-label">{stage.name}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Filter Bar */}
                {selectedSubStage && (
                    <div className="design-filter-bar">
                        <span className="design-filter-label">
                            Showing: {getSubStageName(selectedSubStage)}
                        </span>
                        <span className="design-filter-count">{filteredDeals.length} deals</span>
                        <button className="design-filter-clear" onClick={() => setSelectedSubStage(null)}>
                            Clear Filter
                        </button>
                    </div>
                )}

                {/* Stage Color Key */}
                <div className="design-stage-key">
                    <span className="design-stage-key-label">Stages:</span>
                    {(Object.keys(SUB_STAGE_COLORS) as DesignSubStage[]).map(stage => (
                        <span key={stage} className="design-stage-key-item">
                            <span className="design-stage-key-dot" style={{ background: SUB_STAGE_COLORS[stage] }} />
                            {SUB_STAGE_LABELS[stage]}
                        </span>
                    ))}
                </div>

                {/* Cards Grid */}
                {filteredDeals.length === 0 ? (
                    <div className="design-empty-state">
                        <Inbox size={48} />
                        <h3>{selectedSubStage ? 'No deals in this sub-stage' : 'No deals in design'}</h3>
                        <p>Deals moved to the Design stage will appear here with sub-stage tracking.</p>
                    </div>
                ) : (
                    <div className="design-cards-grid">
                        {filteredDeals
                            .slice()
                            .sort((a, b) => {
                                // Sort by deadline urgency: closest deadline first
                                const deadA = getDeadlineInfo(a);
                                const deadB = getDeadlineInfo(b);
                                if (deadA && deadB) return deadA.businessDaysLeft - deadB.businessDaysLeft;
                                if (deadA) return -1;
                                if (deadB) return 1;
                                return 0;
                            })
                            .map(deal => {
                                const sub = (deal as any).designSubStage as DesignSubStage | undefined;
                                const designType = (deal as any).designType as string | undefined;
                                const designFee = (deal as any).designFee as number | undefined;
                                const progress = getProgress(sub);
                                const daysInStage = getDaysInStage(deal);
                                const deadline = getDeadlineInfo(deal);

                                return (
                                    <div
                                        key={deal.id}
                                        className="design-deal-card"
                                        onClick={() => handleCardClick(deal)}
                                    >
                                        <div className="design-card-header">
                                            <div>
                                                <div className="design-card-name-row">
                                                    <span
                                                        className="design-stage-pip"
                                                        style={{ background: SUB_STAGE_COLORS[sub || 'payment-requested'] }}
                                                    />
                                                    <div className="design-card-name">{deal.contactName}</div>
                                                </div>
                                                <div className="design-card-project">{getProjectDisplayName(deal) || deal.projectType}</div>
                                            </div>
                                            <div className="design-card-badges">
                                                {designType && (
                                                    <span className={`design-type-badge ${designType}`}>
                                                        {designType === 'concept' ? '$500' : '$1,600'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Progress */}
                                        <div className="design-card-progress">
                                            <div className="design-progress-bar-bg">
                                                <div
                                                    className="design-progress-bar-fill"
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <div className="design-progress-label">
                                                <span className="design-substage-name">{getSubStageName(sub)}</span>
                                                <span className="design-days-in-stage">
                                                    <Clock size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                                                    {daysInStage >= 999 ? '999+' : `${daysInStage}d`}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Designer assignment */}
                                        <div className="design-card-architect" onClick={e => e.stopPropagation()}>
                                            <User size={12} className="design-architect-icon" />
                                            <select
                                                className="design-architect-select"
                                                value={getAssignedDesigner(deal, crmUsers).uid}
                                                onChange={e => {
                                                    if (deal.id) handleAssignDesigner(deal.id, e.target.value);
                                                }}
                                            >
                                                <option value="">Assign designer</option>
                                                {designers.map(a => (
                                                    <option key={a.uid} value={a.uid}>{a.displayName}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Footer */}
                                        <div className="design-card-footer">
                                            <div className="design-card-footer-left">
                                                <span className="design-card-value">
                                                    {deal.value > 0 ? `$${deal.value.toLocaleString()}` : designFee ? `$${designFee.toLocaleString()}` : '—'}
                                                </span>
                                                {/* Deadline badge */}
                                                {deadline ? (
                                                    <span
                                                        className={`design-deadline-badge ${deadline.businessDaysLeft <= 0 ? 'overdue' :
                                                            deadline.businessDaysLeft <= 3 ? 'urgent' : 'ok'
                                                            }`}
                                                        title={`Due: ${deadline.deadlineDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — Click to edit payment date`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const input = document.createElement('input');
                                                            input.type = 'date';
                                                            input.style.position = 'fixed';
                                                            input.style.opacity = '0';
                                                            input.style.pointerEvents = 'none';
                                                            const pd = (deal as any).designPaymentDate;
                                                            const dateObj = pd instanceof Date ? pd : pd?.toDate?.();
                                                            if (dateObj) {
                                                                input.value = dateObj.toISOString().split('T')[0];
                                                            }
                                                            document.body.appendChild(input);
                                                            input.showPicker();
                                                            input.addEventListener('change', () => {
                                                                if (deal.id && input.value) {
                                                                    handlePaymentDateChange(deal.id, input.value);
                                                                }
                                                                document.body.removeChild(input);
                                                            });
                                                            input.addEventListener('blur', () => {
                                                                if (document.body.contains(input)) {
                                                                    document.body.removeChild(input);
                                                                }
                                                            });
                                                        }}
                                                    >
                                                        <CalendarClock size={10} style={{ marginRight: 2 }} />
                                                        {deadline.businessDaysLeft <= 0
                                                            ? `${Math.abs(deadline.businessDaysLeft)}d over`
                                                            : `${deadline.businessDaysLeft} BD`
                                                        }
                                                    </span>
                                                ) : (sub && sub !== 'payment-requested') ? (
                                                    <span
                                                        className="design-deadline-badge none"
                                                        title="Set payment date to start deadline countdown"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const input = document.createElement('input');
                                                            input.type = 'date';
                                                            input.style.position = 'fixed';
                                                            input.style.opacity = '0';
                                                            input.style.pointerEvents = 'none';
                                                            input.value = new Date().toISOString().split('T')[0];
                                                            document.body.appendChild(input);
                                                            input.showPicker();
                                                            input.addEventListener('change', () => {
                                                                if (deal.id && input.value) {
                                                                    handlePaymentDateChange(deal.id, input.value);
                                                                }
                                                                document.body.removeChild(input);
                                                            });
                                                            input.addEventListener('blur', () => {
                                                                if (document.body.contains(input)) {
                                                                    document.body.removeChild(input);
                                                                }
                                                            });
                                                        }}
                                                    >
                                                        <CalendarClock size={10} style={{ marginRight: 2 }} />
                                                        Set
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="design-card-actions">
                                                <button
                                                    className="design-action-btn"
                                                    title="Previous sub-stage"
                                                    disabled={!sub || sub === 'payment-requested'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (deal.id) regressSubStage(deal.id, sub);
                                                    }}
                                                >
                                                    <ChevronLeft size={14} />
                                                </button>
                                                <button
                                                    className="design-action-btn"
                                                    title={sub === 'design-complete' ? 'Move to Estimated' : 'Next sub-stage'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (deal.id) advanceSubStage(deal.id, sub);
                                                    }}
                                                >
                                                    <ChevronRight size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                )}
            </div>
            <HubQuickSearch
                open={showQuickSearch}
                onClose={() => setShowQuickSearch(false)}
                getResults={getDesignQuickSearchResults}
                onSelect={handleDesignQuickSearchSelect}
                placeholder="Search design deals…"
                ariaLabel="Design tracker quick search"
                renderIcon={() => <PenTool size={16} />}
            />
        </div>
    );
};

export default DesignTracker;
